import * as CubeTree from '../../../cubeTree/index';
import * as UIToolkit from '../../../UIToolkit';
import * as Toolkit from '../../../toolkit/index';
import Utils from '../../../utils/util';
import CodiSystem from '../../../codiSystem/index';
import { layerLink } from '../../layerGroup/link';
import { findNextLayerCube } from '../funcs/findNextLayerCube';

const colStep: number = 0.01;

function makeMoveTask(arrow: GoodsInsertArrow, dragCubes: CubeId[], transfer: Transfer): MoveTask {
    let result: MoveTask = {
        logic: [],
        physics: []
    };
    let colCubes: CubeId[][] = UIToolkit.getCabinetColCubes(dragCubes);

    result.logic = makeLogicTask(arrow.insertInfo, colCubes);
    result.physics = makePhysicsTask(arrow.insertInfo, colCubes, transfer);

    return result;
}

function makeLogicTask(arrow: GoodsInsertArrow, dragCubes: CubeId[][]): LogicMoveTask[] {
    // 目标点是层板
    if (CubeTree.isCabinet(arrow.target)) {
        return makeMoveToCabinetTask(arrow.target, dragCubes);
    }

    // 目标点是商品、或者文本框
    if (CubeTree.isGoods(arrow.target)
        || CubeTree.isTextarea(arrow.target)) {
        return moveToLayerCube(arrow, dragCubes);
    }

    // 目标点是画布
    if (CubeTree.isSketchpad(arrow.target)) {
        return makeMoveToStageTask(arrow, dragCubes);
    }

    return [];
}

function makePhysicsTask(arrow: GoodsInsertArrow, cols: CubeId[][], transfer: Transfer): PhysicsMoveTask[] {
    if (!arrow.target) {
        return [];
    }

    if (CubeTree.isSketchpad(arrow.target)) {
        return makeMoveToStagePhysicsTask(arrow, transfer);
    }

    if ((CubeTree.isGoods(arrow.target)
        || CubeTree.isTextarea(arrow.target))
        && (arrow.direction === 'top'
        || arrow.direction === 'bottom')) {
        return moveToY(arrow, cols);
    }

    if ((CubeTree.isGoods(arrow.target)
        || CubeTree.isTextarea(arrow.target))
        && (arrow.direction === 'left'
        || arrow.direction === 'right')) {
        return moveToX(arrow, cols);
    }

    if (CubeTree.isCabinet(arrow.target)) {
        return moveToCabinetPhysicsTask(arrow, cols);
    }

    return [];
}

function moveToY(arrow: GoodsInsertArrow, cols: CubeId[][]): PhysicsMoveTask[] {
    let result: PhysicsMoveTask[] = [];

    cols.forEach((cubes: CubeId[]): void => {
        cubes.forEach((cubeId: CubeId): void => {
            let from: PhysicsCoordinate = CodiSystem.getRelativePhysicsCoordinate(cubeId);
            let relative: PhysicsCoordinate = CodiSystem.getRelativePhysicsCoordinate(arrow.target);
            let to: PhysicsCoordinate = relative;
            let toBottom: PhysicsMeasurement = to.bottom;

            if (arrow.direction === 'top') {
                toBottom = Utils.math(to.bottom, to.height, '+');
            }

            result.push({
                from: {
                    moveTarget: cubeId,
                    position: {
                        left: from.left,
                        bottom: from.bottom
                    }
                },
                to: {
                    moveTarget: cubeId,
                    position: {
                        left: to.left,
                        bottom: toBottom
                    }
                }
            });
        });
    });

    return result;
}

function moveToX(arrow: GoodsInsertArrow, cols: CubeId[][]): PhysicsMoveTask[] {
    let result: PhysicsMoveTask[] = [];
    let dis: number = 0;

    cols.forEach((cubes: CubeId[], index: number): void => {
        let target: PhysicsCoordinate = CodiSystem.getRelativePhysicsCoordinate(arrow.target);
        let left: PhysicsMeasurement = 0;

        dis = Utils.math(dis, colStep, '+');

        if (arrow.direction === 'left') {
            left = Utils.math(target.left, dis, '-');
        }
        else {
            left = Utils.math(target.left, dis, '+');
        }

        cubes.forEach((cubeId: CubeId): void => {
            result.push({
                from: {
                    moveTarget: cubeId,
                    position: {
                        left: 0,
                        bottom: 0
                    }
                },
                to: {
                    moveTarget: cubeId,
                    position: {
                        left: left,
                        bottom: 0
                    }
                }
            });
        });
    });

    return result;
}

function moveToCabinetPhysicsTask(arrow: GoodsInsertArrow, cols: CubeId[][]): PhysicsMoveTask[] {
    let result: PhysicsMoveTask[] = [];
    let dis: number = 0;
    let lastChild: CubeId|null = CubeTree.findTheLastChildCubeId(arrow.target);
    let left: PhysicsMeasurement = 0;

    if (lastChild) {
        let lastChildCoordinate: PhysicsCoordinate = CodiSystem.getRelativePhysicsCoordinate(lastChild);

        left = lastChildCoordinate.left;
    }

    cols.forEach((cubes: CubeId[], index: number): void => {
        dis = Utils.math(dis, colStep, '+');
        left = Utils.math(left, dis, '+');

        cubes.forEach((cubeId: CubeId): void => {
            result.push({
                from: {
                    moveTarget: cubeId,
                    position: {
                        left: 0,
                        bottom: 0
                    }
                },
                to: {
                    moveTarget: cubeId,
                    position: {
                        left: left,
                        bottom: 0
                    }
                }
            });
        });
    });

    return result;
}

function makeMoveToStagePhysicsTask(arrow: GoodsInsertArrow, transfer: Transfer): PhysicsMoveTask[] {
    let result: PhysicsMoveTask[] = [];
    let dragCubeCoordinates: CubePaintCoordinate[] = transfer.getDragCubePaintCoordinate();

    dragCubeCoordinates.forEach((coordinate: CubePaintCoordinate): void => {
        let from: PhysicsCoordinate = CodiSystem.getRelativePhysicsCoordinate(coordinate.id);
        let to: PhysicsCoordinate = CodiSystem.paintToPhysics({
            left: coordinate.left,
            top: coordinate.top,
            width: coordinate.width,
            height: coordinate.height
        });

        result.push({
            from: {
                moveTarget: coordinate.id,
                position: {
                    left: from.left,
                    bottom: from.bottom
                }
            },
            to: {
                moveTarget: coordinate.id,
                position: {
                    left: to.left,
                    bottom: to.bottom
                }
            }
        });
    });

    return result;
}

// 移动至舞台，需要判断逻辑关系是否发生变化，如果没发生变化，则不需要做逻辑移动
function makeMoveToStageTask(arrow: GoodsInsertArrow, cols: CubeId[][]): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];
    let dragCubes: CubeId[] = Utils.expandArray(cols);

    dragCubes.forEach((cubeId: CubeId): void => {
        let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(cubeId);
        let oldBefore: CubeId|null = findNextLayerCube(cubeId, dragCubes);

        result.push({
            from: {
                parent: oldParent,
                moveTarget: cubeId,
                before: oldBefore
            },
            to: {
                parent: arrow.target,
                moveTarget: cubeId,
                insertBefore: null
            }
        });
    });

    return result;
}

function moveToLayerCube(arrow: GoodsInsertArrow, cols: CubeId[][]): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];
    let dragCubes: CubeId[] = Utils.expandArray(cols);

    cols.forEach((cubes: CubeId[]): void => {
        cubes.forEach((cubeId: CubeId): void => {
            let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(cubeId);
            let oldBefore: CubeId|null = findNextLayerCube(cubeId, dragCubes);
            let targetLayer: CubeId = CubeTree.findCubeLayer(arrow.target);
            let insertTarget: CubeId|null = arrow.target;

            if (arrow.direction === 'right' || arrow.direction === 'top') {
                insertTarget = CubeTree.findNextCube(arrow.target, dragCubes);
            }

            result.push({
                from: {
                    parent: oldParent,
                    moveTarget: cubeId,
                    before: oldBefore
                },
                to: {
                    parent: targetLayer,
                    moveTarget: cubeId,
                    insertBefore: insertTarget
                }
            });
        });
    });

    return result;
}

function makeMoveToCabinetTask(cabinet: CubeId, colCubes: CubeId[][]): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];
    let dragCubes: CubeId[] = Utils.expandArray(colCubes);

    colCubes.forEach((ids: CubeId[]): void => {
        ids.forEach((cubeId: CubeId): void => {
            let itsParent: CubeId = CubeTree.findParentIdByCubeId(cubeId);
            let oldBefore: CubeId|null = findNextLayerCube(cubeId, dragCubes);

            result.push({
                from: {
                    parent: itsParent,
                    moveTarget: cubeId,
                    before: oldBefore
                },
                to: {
                    parent: cabinet,
                    moveTarget: cubeId,
                    insertBefore: null
                }
            });
        });
    });

    return result;
}

export {
    makeMoveTask
};
